import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../context/SubscriptionContext';
import { Button } from './ui/button';
import { X, AlertTriangle, Clock, CreditCard, Zap } from 'lucide-react';

/**
 * SubscriptionBanner - Shows alerts for subscription issues
 * Displays at the top of the app for:
 * - Payment failures (past_due)
 * - Grace period countdown
 * - Trial ending soon
 * - Cancellation pending
 */
export const SubscriptionBanner = () => {
  const { subscription, loading, isAdmin } = useSubscription();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = React.useState(false);

  // Don't show for admin/legacy or while loading
  if (loading || isAdmin || !subscription?.has_subscription || dismissed) {
    return null;
  }

  const { payment_issue, grace_period, trial_warning, trial_expired, cancel_at_period_end, current_period_end } = subscription;

  // Priority: Grace period expired > Payment failed > Trial ending > Cancellation pending
  let banner = null;

  // Grace period expired - CRITICAL
  if (grace_period?.expired) {
    banner = {
      type: 'critical',
      icon: AlertTriangle,
      title: 'Access Restricted',
      message: 'Your grace period has expired. Please update your payment method to restore access.',
      action: 'Update Payment',
      onAction: () => navigate('/pricing'),
      bgColor: 'bg-red-600',
      textColor: 'text-white',
    };
  }
  // In grace period - URGENT
  else if (grace_period?.in_grace_period) {
    const hoursLeft = Math.round(grace_period.hours_remaining);
    banner = {
      type: 'urgent',
      icon: Clock,
      title: 'Payment Failed',
      message: `Update your payment method within ${hoursLeft} hours to avoid service interruption.`,
      action: 'Update Payment',
      onAction: () => navigate('/pricing'),
      bgColor: 'bg-amber-500',
      textColor: 'text-white',
    };
  }
  // Payment issue without grace period yet
  else if (payment_issue?.type === 'payment_failed') {
    banner = {
      type: 'warning',
      icon: CreditCard,
      title: 'Payment Issue',
      message: payment_issue.message,
      action: 'Update Payment',
      onAction: () => navigate('/pricing'),
      bgColor: 'bg-amber-500',
      textColor: 'text-white',
    };
  }
  // Trial expired - CRITICAL (no card was required to start, but now needed to continue)
  else if (trial_expired || trial_warning?.type === 'trial_expired') {
    banner = {
      type: 'critical',
      icon: CreditCard,
      title: 'Free Trial Ended',
      message: trial_warning?.message || 'Your 30-day free trial has ended. Add a credit card to continue. Your data is retained for 90 days.',
      action: 'Add Credit Card',
      onAction: () => navigate('/manage-subscription'),
      bgColor: 'bg-red-600',
      textColor: 'text-white',
    };
  }
  // Trial ending soon
  else if (trial_warning) {
    banner = {
      type: 'info',
      icon: Zap,
      title: `Trial Ending${trial_warning.days_remaining === 0 ? ' Today' : ''}`,
      message: trial_warning.message,
      action: 'Add Payment Method',
      onAction: () => navigate('/manage-subscription'),
      bgColor: 'bg-blue-600',
      textColor: 'text-white',
      dismissible: true,
    };
  }
  // Cancellation pending
  else if (cancel_at_period_end && current_period_end) {
    const endDate = new Date(current_period_end).toLocaleDateString();
    banner = {
      type: 'info',
      icon: AlertTriangle,
      title: 'Subscription Canceling',
      message: `Your subscription will end on ${endDate}. You can reactivate anytime before then.`,
      action: 'Reactivate',
      onAction: () => navigate('/pricing'),
      bgColor: 'bg-slate-700',
      textColor: 'text-white',
      dismissible: true,
    };
  }

  if (!banner) {
    return null;
  }

  const Icon = banner.icon;

  return (
    <div className={`${banner.bgColor} ${banner.textColor} px-4 py-3`} data-testid="subscription-banner">
      <div className="container mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Icon className="h-5 w-5 flex-shrink-0" />
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
            <span className="font-semibold">{banner.title}:</span>
            <span className="text-sm opacity-95">{banner.message}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            className="bg-white/20 hover:bg-white/30 text-current border-0"
            onClick={banner.onAction}
          >
            {banner.action}
          </Button>
          {banner.dismissible && (
            <button
              onClick={() => setDismissed(true)}
              className="p-1 hover:bg-white/20 rounded"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionBanner;
